import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Calendar, 
  Plus, 
  Trash2, 
  Edit2, 
  Loader2,
  CalendarX,
  CalendarCheck,
  AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAvailabilityCalendar = () => {
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRange, setEditingRange] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    status: "BLOCKED",
    note: ""
  });

  useEffect(() => {
    fetchRanges();
  }, []);

  const fetchRanges = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRanges(response.data);
    } catch (error) {
      console.error("Failed to fetch ranges:", error);
      toast.error("Failed to load availability data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (range = null) => {
    if (range) {
      setEditingRange(range);
      setFormData({
        start_date: range.start_date,
        end_date: range.end_date,
        status: range.status,
        note: range.note || ""
      });
    } else {
      setEditingRange(null);
      setFormData({
        start_date: "",
        end_date: "",
        status: "BLOCKED",
        note: ""
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.start_date || !formData.end_date) {
      toast.error("Please select both start and end dates");
      return;
    }

    if (formData.end_date < formData.start_date) {
      toast.error("End date cannot be before start date");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      
      if (editingRange) {
        await axios.put(`${API}/admin/availability/${editingRange.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Date range updated!");
      } else {
        await axios.post(`${API}/admin/availability`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Date range added!");
      }
      
      setShowModal(false);
      fetchRanges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rangeId) => {
    if (!window.confirm("Are you sure you want to delete this date range?")) {
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/availability/${rangeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Date range deleted!");
      fetchRanges();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-IN", { 
      day: "numeric", 
      month: "short", 
      year: "numeric" 
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-[#B08968]" />
              Availability Calendar
            </h1>
            <p className="text-gray-600 mt-1">
              Manage venue availability by blocking or unblocking date ranges
            </p>
          </div>
          
          <Button
            onClick={() => handleOpenModal()}
            className="gap-2 bg-[#B08968] hover:bg-[#9A7558]"
          >
            <Plus className="w-4 h-4" />
            Add Date Range
          </Button>
        </div>

        {/* Info Card */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">How it works:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>By default, all dates are considered <strong>available</strong></li>
                <li>Add <strong>BLOCKED</strong> ranges to mark dates as unavailable</li>
                <li>Customers cannot book dates that overlap with blocked ranges</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Ranges List */}
        {ranges.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <CalendarCheck className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">All Dates Available</h3>
            <p className="text-gray-500 mb-4">
              No blocked date ranges. All dates are currently available for booking.
            </p>
            <Button onClick={() => handleOpenModal()} className="bg-[#B08968] hover:bg-[#9A7558]">
              Block Dates
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Start Date</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">End Date</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Note</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ranges.map((range) => (
                    <tr key={range.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          range.status === "BLOCKED" 
                            ? "bg-red-100 text-red-700" 
                            : "bg-green-100 text-green-700"
                        }`}>
                          {range.status === "BLOCKED" ? (
                            <CalendarX className="w-3 h-3" />
                          ) : (
                            <CalendarCheck className="w-3 h-3" />
                          )}
                          {range.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(range.start_date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(range.end_date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {range.note || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenModal(range)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(range.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingRange ? "Edit Date Range" : "Add Date Range"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    min={formData.start_date}
                  />
                </div>
              </div>

              <div>
                <Label>Status</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      value="BLOCKED"
                      checked={formData.status === "BLOCKED"}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="text-[#B08968]"
                    />
                    <span className="flex items-center gap-1.5">
                      <CalendarX className="w-4 h-4 text-red-500" />
                      Blocked
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      value="AVAILABLE"
                      checked={formData.status === "AVAILABLE"}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="text-[#B08968]"
                    />
                    <span className="flex items-center gap-1.5">
                      <CalendarCheck className="w-4 h-4 text-green-500" />
                      Available
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <Label>Note (Optional)</Label>
                <Textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="e.g., Private booking, Maintenance, etc."
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-[#B08968] hover:bg-[#9A7558]"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingRange ? "Update" : "Add"} Range
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminAvailabilityCalendar;
